import { useState, type FormEvent } from 'react';
import { motion } from 'motion/react';
import { Container, Kicker, Mark } from '@/components/primitives';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const LINKEDIN_URL = 'https://www.linkedin.com/company/113139110/';

const TRAITS = [
  {
    n: '01',
    t: 'Voglia matta di spaccare',
    d: 'Non "impegno". Non "dedizione". Voglia matta. Se ti va bene il compitino fatto bene, non fa per te.',
  },
  {
    n: '02',
    t: 'Voglia di apprendere',
    d: 'Qui si impara più in un mese che altrove in un anno. Chi si accontenta di quello che sa già resta indietro.',
  },
  {
    n: '03',
    t: 'Curiosità e passione vere',
    d: 'Per quello che si fa, non per il logo sulla felpa. Il resto — competenze, strumenti, mestiere — lo costruiamo insieme.',
  },
];

const AMBITI = [
  'Ingegneria & Prodotto',
  'Vendite & Growth',
  'Operazioni di cantiere',
  'Altro',
];

interface FormState {
  nome: string;
  email: string;
  ambito: string;
  link: string;
  messaggio: string;
}

const EMPTY_FORM: FormState = { nome: '', email: '', ambito: AMBITI[0], link: '', messaggio: '' };

function buildMailto(data: FormState): string {
  const subject = `Candidatura spontanea — ${data.nome}`;
  const body = [
    `Nome: ${data.nome}`,
    `Email: ${data.email}`,
    `Ambito di interesse: ${data.ambito}`,
    data.link ? `LinkedIn / portfolio: ${data.link}` : null,
    '',
    'Messaggio:',
    data.messaggio,
  ]
    .filter((line): line is string => line !== null)
    .join('\n');

  return `mailto:m.angelozzi@bossai.build?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

const inputClass =
  'w-full rounded-lg border border-input bg-background/40 px-4 py-3 text-[14px] text-foreground placeholder:text-muted-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring';

export default function CandidaturePage() {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    window.location.href = buildMailto(form);
  }

  return (
    <>
      {/* Hero */}
      <section className="relative isolate overflow-hidden pt-36 pb-20 md:pt-44 md:pb-28">
        <div
          aria-hidden
          className="absolute inset-0 -z-10"
          style={{
            background:
              'radial-gradient(50% 50% at 50% -10%, color-mix(in oklch, var(--primary) 18%, transparent), transparent 70%)',
          }}
        />
        <Container className="relative">
          <Badge variant="soft">· Candidature</Badge>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="boss-display mt-6 max-w-[20ch] text-balance text-[44px] md:text-[80px]"
          >
            Le eccellenze del Sud. Un progetto internazionale. <Mark>Fatto qui.</Mark>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.9 }}
            className="mt-7 max-w-2xl text-pretty text-[18px] leading-[1.55] text-fg-2"
          >
            Le teste migliori del Sud, di solito, le vogliono altrove — Milano, Londra, un ufficio dove non
            si torna più a casa. Noi vogliamo il contrario: mettere insieme quella gente e costruire qui
            qualcosa di internazionale, ambizioso, che funziona sul serio. Non un progettino, non un
            consolation prize per chi "non è voluto partire". Un'azienda vera, fatta al Sud.
          </motion.p>
        </Container>
      </section>

      {/* Traits */}
      <section className="relative border-t border-border py-20 md:py-28">
        <Container>
          <Kicker index="01" label="Chi cerchiamo" />
          <p className="mt-6 max-w-2xl text-pretty text-[16px] leading-[1.55] text-fg-2">
            Non guardiamo il CV, la laurea, gli anni di esperienza. Le condizioni sono tre, sono le uniche che
            ci interessano, e non sono negoziabili:
          </p>
          <div className="mt-10 grid grid-cols-1 gap-6 border-t border-border pt-9 md:grid-cols-3">
            {TRAITS.map((item) => (
              <div key={item.n}>
                <div className="font-mono text-[11px] text-primary">{item.n}</div>
                <div className="boss-h1 mt-2 text-[19px]">{item.t}</div>
                <p className="mt-2 text-pretty text-[14px] leading-[1.6] text-fg-2">{item.d}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Application form */}
      <section id="candidatura" className="relative border-t border-border py-20 md:py-28">
        <Container>
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_1.3fr] lg:gap-20">
            <div>
              <Kicker index="02" label="Candidatura libera" />
              <h2 className="boss-display mt-5 max-w-md text-balance text-[32px] md:text-[44px]">
                Se ti riconosci in questo, <Mark>scrivici.</Mark>
              </h2>
              <p className="mt-6 max-w-md text-pretty text-[15px] leading-[1.6] text-fg-2">
                Compila il modulo: si apre la tua mail già pronta, con tutto quello che ci serve. Niente
                account da creare, niente portali. Se preferisci, scrivici direttamente a{' '}
                <a href="mailto:m.angelozzi@bossai.build" className="text-foreground underline underline-offset-4">
                  m.angelozzi@bossai.build
                </a>
                .
              </p>
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-2 text-[14px] text-fg-2 transition-colors hover:text-primary"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.38-1.85 3.61 0 4.28 2.38 4.28 5.47v6.27zM5.34 7.43a2.07 2.07 0 1 1 0-4.14 2.07 2.07 0 0 1 0 4.14zM7.12 20.45H3.56V9h3.56v11.45z" />
                </svg>
                Seguici su LinkedIn
              </a>
            </div>

            <motion.form
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7 }}
              onSubmit={handleSubmit}
              className="rounded-[24px] border border-input bg-card p-8 md:p-10"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <label className="flex flex-col gap-2 text-[13px] text-fg-2">
                  Nome e cognome
                  <input
                    required
                    type="text"
                    value={form.nome}
                    onChange={(e) => update('nome', e.target.value)}
                    className={inputClass}
                    placeholder="Nome Cognome"
                  />
                </label>
                <label className="flex flex-col gap-2 text-[13px] text-fg-2">
                  Email
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                    className={inputClass}
                    placeholder="tu@email.it"
                  />
                </label>
              </div>

              <label className="mt-5 flex flex-col gap-2 text-[13px] text-fg-2">
                Ambito di interesse
                <select
                  value={form.ambito}
                  onChange={(e) => update('ambito', e.target.value)}
                  className={inputClass}
                >
                  {AMBITI.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </label>

              <label className="mt-5 flex flex-col gap-2 text-[13px] text-fg-2">
                LinkedIn o portfolio (opzionale)
                <input
                  type="url"
                  value={form.link}
                  onChange={(e) => update('link', e.target.value)}
                  className={inputClass}
                  placeholder="https://linkedin.com/in/…"
                />
              </label>

              <label className="mt-5 flex flex-col gap-2 text-[13px] text-fg-2">
                Raccontaci in due righe perché fa per te
                <textarea
                  required
                  rows={4}
                  value={form.messaggio}
                  onChange={(e) => update('messaggio', e.target.value)}
                  className={inputClass}
                  placeholder="Zero fuffa, dritti al punto."
                />
              </label>

              <Button type="submit" size="lg" className="mt-7 w-full sm:w-auto">
                Invia candidatura →
              </Button>
            </motion.form>
          </div>
        </Container>
      </section>
    </>
  );
}
